import { graphHooks } from '@tunasong/graph-lib/react'
import { logger } from '@tunasong/models'
import { entitiesApi } from '@tunasong/redux'
import type { Entity, Persisted, Profile } from '@tunasong/schemas'
import type { EntityListItemProps } from '@tunasong/ui-lib'
import { AvatarStack, metaSeparator, useDimensions } from '@tunasong/ui-lib'
import { useEffect, useState } from 'react'
import type { SpaceChild } from './space-meta.js'
import {
  chunkIds,
  getSpaceMembers,
  guestEmail,
  isGuestPrincipal,
  spaceActivityAt,
  spaceMetaParts,
  summarizeSpaceChildren,
} from './space-meta.js'

/** What the counts need. The server adds userId and acls for its READ filter; no storage, so nothing is signed. */
const childFields = 'id,type,parentId,updatedAt,tags'

/** A space is shared once someone besides its owner is in it */
const minMembersForStack = 2

export type SpaceRowProps = Pick<EntityListItemProps, 'meta' | 'trailing' | 'activityAt'>

interface ChildrenResult {
  /** The id set these children answer for */
  key: string
  children: SpaceChild[]
}

/**
 * The children of every space, fetched in parallel chunks of 50 ids. Every id rides in the URL, so a single
 * request would fail past about 200 spaces.
 * - A failed chunk costs only its own spaces their counts.
 * - `settled` is true once every chunk for the current ids has answered.
 * - While a changed id set (say, a new space) refetches, the previous counts stay up rather than blink back to
 *   the kind.
 */
const useSpacesChildren = (parentIds: string[]) => {
  const [trigger] = entitiesApi.useLazyLoadChildEntitiesBatchQuery()
  const [result, setResult] = useState<ChildrenResult | null>(null)
  const key = parentIds.join(',')

  useEffect(() => {
    if (!key) {
      return
    }
    let cancelled = false
    const load = async () => {
      const outcomes = await Promise.allSettled(
        chunkIds(key.split(',')).map(ids => trigger({ parentIds: ids, fields: childFields }, true).unwrap())
      )
      if (cancelled) {
        return
      }
      const children = outcomes.flatMap(outcome => {
        if (outcome.status === 'rejected') {
          logger.warn('Home Spaces: a counts chunk failed; its spaces show their kind only', outcome.reason)
          return []
        }
        /** A lazy RTK `unwrap()` can resolve undefined: no children, as for a failed chunk (GH #2684) */
        return outcome.value ?? []
      })
      setResult({ key, children })
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [key, trigger])

  return { children: result?.children, settled: key === '' || result?.key === key }
}

/**
 * Counts, members and last activity for Home's Spaces rows.
 * - Children cost one request per 50 spaces, and every member profile costs one request in total. That is never
 *   one request per space.
 * - Until the counts arrive, or if they fail, a row's meta line is its kind alone.
 *
 * @see docs/superpowers/specs/2026-10-17-home-spaces-meta-design.md
 */
export const useSpacesMeta = (spaces: Persisted<Entity>[]) => {
  const { isSmallOrSmaller } = useDimensions()

  // Sorted, so neither the request nor the cache key depends on the display order
  const parentIds = spaces.map(space => space.id).sort()
  const { children, settled } = useSpacesChildren(parentIds)
  const summaries = children ? summarizeSpaceChildren(children) : undefined

  const membersBySpace = new Map(spaces.map(space => [space.id, getSpaceMembers(space)]))
  const sharedMemberIds = [...membersBySpace.values()].filter(members => members.length >= minMembersForStack).flat()
  // Phones show no stack, so they load no profiles
  const profileIds = isSmallOrSmaller ? [] : [...new Set(sharedMemberIds.filter(id => !isGuestPrincipal(id)))].sort()
  const { profiles } = graphHooks.useProfiles({ userIds: profileIds, includeAssistant: false })
  const profileById = new Map(profiles.map(profile => [profile.userId, profile]))

  const toProfile = (id: string): Partial<Profile> =>
    isGuestPrincipal(id) ? { userId: id, email: guestEmail(id) } : (profileById.get(id) ?? { userId: id })

  const activityOf = (space: Persisted<Entity>) => spaceActivityAt(space, summaries?.get(space.id))

  const getItemProps = (space: Persisted<Entity>): SpaceRowProps => {
    const members = membersBySpace.get(space.id) ?? []
    const showMembers = !isSmallOrSmaller && members.length >= minMembersForStack
    return {
      meta: spaceMetaParts(space.type, summaries?.get(space.id)).join(metaSeparator),
      trailing: showMembers ? <AvatarStack members={members.map(toProfile)} /> : undefined,
      activityAt: activityOf(space),
    }
  }

  return { getItemProps, activityOf, settled }
}
